import { Navigate } from 'react-router';
import type { ReactNode } from 'react';
import { useAuth } from '@/lib/use-auth';
import type { UserRole } from '@/lib/schemas/user';
import { RequireAuth } from './RequireAuth';

interface RequireRoleProps {
  children: ReactNode;
  role: UserRole;
  /** Where to send wrong-role users. Defaults to `/`. */
  fallbackTo?: string;
}

/**
 * Wraps `RequireAuth` and additionally enforces a specific role. Parents
 * hitting `/onboarding/*` get bounced home; tutors hitting parent-only routes
 * (M8+) will get the same treatment. Used so individual pages don't repeat the
 * "guest → /login, wrong role → /" pattern by hand.
 */
export function RequireRole({ children, role, fallbackTo = '/' }: RequireRoleProps) {
  return (
    <RequireAuth>
      <RoleGate role={role} fallbackTo={fallbackTo}>
        {children}
      </RoleGate>
    </RequireAuth>
  );
}

function RoleGate({
  children,
  role,
  fallbackTo,
}: {
  children: ReactNode;
  role: UserRole;
  fallbackTo: string;
}) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== role) {
    return <Navigate to={fallbackTo} replace />;
  }
  return <>{children}</>;
}
